import Vue from 'vue'
import Router from 'vue-router'
import store from './../store/index'
import { EventBus } from '@/event-bus'
import { isUpdatePending, isReloadBlocked, checkForUpdate, isStaleChunkError, recoverFromStaleChunk, reloadAt, setStaleChunkHandler } from '@/utils/appVersion'
import translations from './../locales/index'
import { trackClarityPage } from '@/utils/clarity'
Vue.use(Router)

// A router guard/afterEach isn't a component - there's no `this.$t`/`this.$i18n` to call here, and
// the app's VueI18n instance (main.js) isn't exported for reuse. Reads the same raw locale JSON
// main.js itself loads (src/locales/index.js) with the identical locale-detection order it uses,
// rather than duplicating/exporting the whole i18n bootstrap just for this one popup.
function translate(key) {
  const locale = localStorage.getItem('language')
    || navigator.languages.find((l) => l.length === 2)
    || navigator.language.split('-')[0]
    || 'fr'
  return (translations[locale] && translations[locale][key]) || (translations.fr && translations.fr[key]) || key
}

// Helper function to check if user has access to Presence Management in PROD
function checkPresenceManagementAccess() {
  if (process.env.NODE_ENV !== 'production') return true

  try {
    const user = localStorage.user && localStorage.user !== 'undefined' ? JSON.parse(localStorage.user) : null
    if (!user) return false

    if (user.email && user.email.toLowerCase().endsWith('@mashup-web.com')) return true

    const authorizedAccounts = [
      '9e046e84-5b26-4c94-8379-9a88ea278490',
      '6a184790-5f10-43eb-8f16-05daa21d6dc5'
    ]
    const authorizedUsers = [
      '9e046e84-5b26-4c94-8379-9a88ea278490'
    ]

    return (user.account && authorizedAccounts.includes(user.account)) ||
           (user.id && authorizedUsers.includes(user.id))
  } catch (e) {
    return false
  }
}

// Helper function to check if user has any connected pages (Helps determine if onboarding is needed for users)
async function checkIfUserHasPages() {
  try {
    const response = await Vue.http.get(
      `${process.env.VUE_APP_API_HOST}/pages`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    )

    if (response.ok && response.body && response.body.pages) {
      return response.body.pages.length > 0
    }

    return false
  } catch (error) {
    console.error('[Router Guard] Error checking pages:', error)
    // On error, assume user has pages to avoid blocking access
    return true
  }
}

// Helper function to fetch user profile
async function fetchUserProfile() {
  try {
    const response = await Vue.http.get(
      `${process.env.VUE_APP_API_HOST}/users/me`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )
    if (response.ok && response.body && response.body.user) {
      localStorage.setItem('user', JSON.stringify(response.body.user))
      return response.body.user
    }
    return null
  } catch (error) {
    console.error('[Router Guard] Error fetching user profile:', error)
    return null
  }
}

// Guards a single attemptResumeDemoDraft() run at a time (see checkResumeDemoDraft's afterEach
// below - navigating rapidly between planner/posts2/postCreator right after login could otherwise
// fire two overlapping attempts against the same still-present localStorage entry).
let resumeCheckInProgress = false

// Resumes a post composed in the public demo (/demo/postCreator, see DemoPostCreator.vue /
// PostCreator.vue's saveDemoDraftAndPromptSignup) once the visitor has signed up and connected at
// least one real page. Remaps each fake demo page id to the visitor's own real page of the same
// network (dropping any network they didn't actually connect), then hands the result to
// postCreator/setDataForDraft exactly like a normal saved draft. Returns true if a draft was
// prepared and the visitor should be invited to resume it, or null otherwise (nothing pending, or
// no real page yet to remap it onto). Deliberately does NOT navigate or show any popup itself -
// see checkResumeDemoDraft below for why (the /dash beforeEnter this used to run from can't
// reliably show a popup: it may fire during a hard reload right after login, wiping this whole
// module's state and any in-memory "show it now" flag before a listener ever mounts to catch it).
async function attemptResumeDemoDraft() {
  const raw = localStorage.getItem('demo/pendingDraft')
  if (!raw) return null

  let saved
  try {
    saved = JSON.parse(raw)
  } catch (error) {
    localStorage.removeItem('demo/pendingDraft')
    return null
  }

  try {
    const response = await Vue.http.get(
      `${process.env.VUE_APP_API_HOST}/pages`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )
    const realPages = (response.ok && response.body && response.body.pages) || []

    const realPageIdByNetwork = {}
    realPages.forEach(page => {
      if (!realPageIdByNetwork[page.network]) realPageIdByNetwork[page.network] = page.id
    })

    const fakeToRealPageId = {}
    ;(saved.demoNetworks || []).forEach((network, index) => {
      const fakePageId = saved.draft && saved.draft.data && saved.draft.data.pages && saved.draft.data.pages[index]
      const realPageId = realPageIdByNetwork[network]
      if (fakePageId && realPageId) fakeToRealPageId[fakePageId] = realPageId
    })

    if (!Object.keys(fakeToRealPageId).length) {
      localStorage.removeItem('demo/pendingDraft')
      return null
    }

    // Simple string swap rather than walking saveAllFormData's internal shape - fake ids
    // (`demo-<brand>-<network>`) are distinctive enough not to collide with anything else in the
    // serialized draft, and this catches any nested reference to a fake page id without needing to
    // know exactly where the post-composition form data keeps them.
    let draftJson = JSON.stringify(saved.draft)
    Object.entries(fakeToRealPageId).forEach(([fakeId, realId]) => {
      draftJson = draftJson.split(fakeId).join(realId)
    })
    const remappedDraft = JSON.parse(draftJson)
    // Pre-select every one of the visitor's own pages on a network the demo draft touched, not
    // just the single arbitrary one fakeToRealPageId above picked to migrate saveAllFormData's
    // per-page/network overrides onto - if they have several Facebook pages connected, they should
    // see (and be able to tick/untick) all of them via the normal page selector below, not have
    // this guess made silently for them. Draft mode never freezes that selector (unlike 'update'),
    // so this is just its starting state.
    const touchedNetworks = new Set(saved.demoNetworks || [])
    remappedDraft.data.pages = realPages.filter(page => touchedNetworks.has(page.network)).map(page => page.id)
    // The demo never uploads media anywhere (see attemptFileUpload's isDemo branch) - its `files`
    // entries only ever hold a browser-local blob: url, which the real backend can't fetch and
    // which dies anyway once this tab/session ends. Rather than resume a draft with images that
    // look present but are actually broken, drop them - text/pages/settings still carry over, the
    // visitor just re-adds their media once back in their own real Post Creator.
    remappedDraft.data.files = {}

    // Deliberately NOT deleting 'demo/pendingDraft' here - whether it should survive this attempt
    // is the visitor's call, made from the popup this feeds into (see the afterEach below):
    // resuming always deletes it, closing only deletes it if they ticked "don't ask again".
    // Leaving it in place also means this function stays safe to call again on their next
    // navigation - it always re-fetches real pages and re-remaps rather than trusting a stale
    // in-memory result.
    store.commit('postCreator/setDataForDraft', remappedDraft)

    return true
  } catch (error) {
    console.error('[Router Guard] Failed to resume demo draft:', error)
    localStorage.removeItem('demo/pendingDraft')
    return null
  }
}

const queryParams = new URLSearchParams(window.location.search)
if (queryParams.get('supervisortoken')) {
  window.$crisp.push(['do', 'session:reset'])
  localStorage.clear()
  localStorage.setItem('token', queryParams.get('supervisortoken'))
  localStorage.setItem('supervisor', true)
  if (queryParams.get('viewer')) window.location = '/#/dash/planner'
  else window.location = '/'
}

if (
  !localStorage.token &&
  ['post_id', 'page_id'].every(p => queryParams.has(p))
)
  localStorage.setItem('redirect_params_url', window.location.href)

// beforeEnter/beforeEach guards call next('/other/path') to redirect, which makes vue-router
// reject the push()/replace() promise (NavigationDuplicated/NavigationRedirected). None of the
// 130+ call sites in this app attach a .catch(), so every guard redirect surfaced in Sentry as
// an unhandled promise rejection (FRONT-SAAS-K, 1X, 1Y) despite being expected routing behavior.
// vue-router only exposes Router.isNavigationFailure/NavigationFailureType from 3.4+; this repo
// is pinned to 3.3.2, which doesn't have it (FRONT-SAAS-E3 regression) - use the internal
// err._isRouter flag that 3.3.2's own createRouterError() sets on all of its navigation errors.
function isRouterNavigationError(err) {
  return !!(err && err._isRouter)
}
const originalPush = Router.prototype.push
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (isRouterNavigationError(err)) return err
    throw err
  })
}
const originalReplace = Router.prototype.replace
Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
  return originalReplace.call(this, location).catch(err => {
    if (isRouterNavigationError(err)) return err
    throw err
  })
}

const router = new Router({
  routes: [
    {
      name: 'underscore',
      path: '/_',
      redirect: '/dash/overview',
      component: () => import('@/views/Dash'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })
        next()
      }
    },
    {
      name: 'dash',
      path: '/dash',
      redirect: '/dash/overview',
      component: () => import('@/views/Dash'),
      beforeEnter: async (_to, _from, next) => {
        if (!localStorage.token) {
          // Preserve the OAuth "remote connector" flow (see engine's /oauth/authorize) across a
          // login - important on mobile, where the user is very likely not already logged in
          // when Claude's "Add custom connector" opens this URL in a browser.
          if (_to.path === '/dash/settings/api_keys' && _to.query.oauth_flow)
            localStorage.setItem('post_login_redirect', _to.fullPath)

          return next({ name: 'auth' })
        }

        const InProduction = true // Enabled for all environments (remove to restrict to local/sandbox only)
        // const InProduction = process.env.NODE_ENV !== 'production'

        let user = null
        let hasPages = false

        if (InProduction) {
          try {
            // Fetch fresh user profile and page count in parallel from the backend
            const [fetchedUser, pagesExist] = await Promise.all([
              fetchUserProfile(),
              checkIfUserHasPages()
            ])
            user = fetchedUser
            hasPages = pagesExist
          } catch (e) {
            console.error('[Router Guard] Error fetching initial data from backend:', e)
          }

          // If the backend is down or returned an invalid response, do not block the user with a payment page.
          // We only show the payment page if we are certain the user has no active subscription.
          if (!user) {
            return next()
          }
        } else {
          // Fallback to cache for development/sandbox where mock/cached user is acceptable
          try {
            const raw = localStorage.getItem('user')
            if (raw && raw !== 'undefined') user = JSON.parse(raw)
          } catch (e) { /* ignore */ }
        }

        // A visitor who composed a post in the public demo and just signed up gets it back via
        // checkResumeDemoDraft's afterEach (see below, near router.afterEach) - triggered when
        // they land on a "post" section (planner/posts2/postCreator), not tied to this exact
        // guard pass, since right after login there's no Dash.vue/componentModal mounted yet to
        // actually show a popup, and login can involve a hard reload that would silently drop it.

        const onboardingCompleted = localStorage.getItem('onboardingCompleted') === 'true'
        const onboardingSkipped = localStorage.getItem('onboardingSkipped') === 'true'

        // If user is mid-onboarding (onboarding_mode flag set by StepConnectPages before OAuth
        // redirect), bypass the page/payment checks entirely and go back to onboarding.
        if (localStorage.getItem('onboarding_mode') === 'true' && !onboardingCompleted && !onboardingSkipped) {
          if (user && user.admin) {
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh') === 'true'
            const searchParams = new URLSearchParams(window.location.search)
            const query = Object.fromEntries(searchParams)
            console.log('[Router Guard /dash] Onboarding mode active — bypassing dash guard, redirecting to onboarding')
            return next(
              isSummaryRefresh
                ? { name: 'onboarding-summary', query }
                : { name: 'onboarding-connectPages', query }
            )
          }
        }

        // If onboarding already done or skipped, check payment regulation then allow dash
        if (onboardingCompleted || onboardingSkipped) {
          if (InProduction && user) {
            const hasActivePlan = user._premium && user._premium.active
            if (!hasActivePlan) {
              if (user.admin) {
                console.log('[Router Guard] Subscription inactive (admin) - redirecting to payment-regulation')
                return next({ name: 'payment-regulation' })
              } else {
                console.log('[Router Guard] Subscription inactive (non-admin) - redirecting to payment-blocked')
                return next({ name: 'payment-blocked' })
              }
            }
          }
          return next()
        }

        if (InProduction && user) {
          const hasActivePlan = user._premium && user._premium.active

          if (!hasActivePlan && hasPages) {
            console.log('[Router Guard] Subscription lapsed - user has pages but no active plan - redirecting to payment page')
            if (user.admin) {
              return next({ name: 'payment-regulation' })
            } else {
              return next({ name: 'payment-blocked' })
            }
          }

          // Non-admin users are never redirected to /onboarding — mark as completed and proceed
          if (!user.admin) {
            localStorage.setItem('onboardingCompleted', 'true')
            return next()
          }

          if (!hasActivePlan) {
            console.log('[Router Guard] User has no active plan - redirecting to onboarding/needs')
            return next({ path: '/onboarding/needs' })
          }

          if (!hasPages) {
            console.log('[Router Guard] User has plan but no pages - redirecting to onboarding/networks')
            return next({ path: '/onboarding/networks' })
          }

          // Active plan + pages: auto-mark onboarding as completed
          // so we skip these checks on subsequent navigations
          localStorage.setItem('onboardingCompleted', 'true')
        }

        next()
      },
      children: [
        {
          path: 'overview',
          name: 'overview',
          component: () => import('@/components/dash/section/Overview'),
          beforeEnter: (to, from, next) => {
            // Intercept OAuth returns in onboarding mode
            const isOnboardingMode = localStorage.getItem('onboarding_mode')
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh')
            const currentNetwork = localStorage.getItem('onboarding_current_network')
            const currentIndex = localStorage.getItem('onboarding_current_index')

            // Check for OAuth callback params
            const queryParams = new URLSearchParams(window.location.search)
            const hasOAuthParams =
              queryParams.has('code') ||
              queryParams.has('oauth_token') ||
              queryParams.has('oauth_verifier')

            const onboardingDone =
              localStorage.getItem('onboardingCompleted') === 'true' ||
              localStorage.getItem('onboardingSkipped') === 'true'

            if (isOnboardingMode === 'true' && hasOAuthParams && !onboardingDone) {
              // Only redirect admin users to onboarding — clear the flag and proceed for non-admins
              let isAdmin = false
              try {
                const raw = localStorage.getItem('user')
                const u = raw && raw !== 'undefined' ? JSON.parse(raw) : null
                isAdmin = !!(u && u.admin)
              } catch (e) { /* ignore */ }

              if (!isAdmin) {
                localStorage.removeItem('onboarding_mode')
              } else if (isSummaryRefresh === 'true') {
                console.log('[Router Guard] Redirecting to onboarding-summary (refresh mode)')
                return next({
                  name: 'onboarding-summary',
                  query: to.query
                })
              } else {
                console.log('[Router Guard] Redirecting to onboarding-connectPages')
                return next({
                  name: 'onboarding-connectPages',
                  query: to.query
                })
              }
            }
            next()
          }
        },
        {
          path: 'pages',
          name: 'pages',
          component: () => import('@/components/dash/section/Pages'),
          beforeEnter: (to, from, next) => {
            // Intercept OAuth returns in onboarding mode BEFORE component mounts
            const isOnboardingMode = localStorage.getItem('onboarding_mode')
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh')
            const onboardingDone =
              localStorage.getItem('onboardingCompleted') === 'true' ||
              localStorage.getItem('onboardingSkipped') === 'true'

            if (isOnboardingMode === 'true' && !onboardingDone) {
              // Only redirect admin users to onboarding — clear the flag and proceed for non-admins
              let isAdmin = false
              try {
                const raw = localStorage.getItem('user')
                const u = raw && raw !== 'undefined' ? JSON.parse(raw) : null
                isAdmin = !!(u && u.admin)
              } catch (e) { /* ignore */ }

              if (!isAdmin) {
                localStorage.removeItem('onboarding_mode')
                // Proceed normally to the Pages component
              } else if (isSummaryRefresh === 'true') {
                console.log('[Router Guard] Summary refresh detected - redirecting to onboarding-summary')
                console.log('[Router Guard] Preserving query params:', to.query)
                return next({
                  name: 'onboarding-summary',
                  query: to.query
                })
              } else {
                console.log('[Router Guard] Onboarding mode detected - redirecting to onboarding-connectPages')
                console.log('[Router Guard] Preserving query params:', to.query)
                console.log('[Router Guard] OAuth callback will be handled in StepConnectPages')
                // Do not remove the flag here, StepConnectPages will handle it
                return next({
                  name: 'onboarding-connectPages',
                  query: to.query
                })
              }
            }
            console.log('[Router Guard] Normal mode - proceeding to Pages component')
            next()
          }
        },
        {
          path: 'bots',
          name: 'bots',
          component: () => import('@/components/dash/section/Bots'),
          props: true,
          alias: ['bots/new', 'bots/edit']
        },
        {
          path: 'posts',
          name: 'posts',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version !== 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/posts2')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts'),
          props: true
        },
        {
          path: 'planner',
          name: 'planner',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version !== 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/posts2')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts'), // Affiche ce composant si aucune redirection
          props: route => ({
            date: route.params.date || route.query.date || null
          })
        },

        {
          path: 'moderation',
          name: 'moderation',
          component: () => import('@/components/dash/section/Moderation'),
          props: true
        },
        {
          path: 'settings/:tab?',
          name: 'settings',
          component: () => import('@/components/dash/section/Settings'),
          alias: ['settings/users', 'settings/user']
        },
        {
          path: 'invoice',
          name: 'invoice',
          component: () => import('@/components/dash/section/Invoice')
        },
        {
          path: 'sources',
          name: 'sources',
          component: () => import('@/components/dash/section/Sources')
        },
        {
          path: 'presence-management',
          name: 'presence-management',
          beforeEnter: (_to, _from, next) => {
            if (!checkPresenceManagementAccess()) return next({ name: 'overview' })
            next()
          },
          component: () => import('@/components/dash/presence-management/PresenceManagement')
        },
        {
          path: 'presence-management-hours',
          name: 'presence-management-hours',
          beforeEnter: (_to, _from, next) => {
            if (!checkPresenceManagementAccess()) return next({ name: 'overview' })
            next()
          },
          component: () => import('@/components/dash/presence-management/BulkHoursEditor')
        },
        {
          path: 'presence-management-special-hours',
          name: 'presence-management-special-hours',
          beforeEnter: (_to, _from, next) => {
            if (!checkPresenceManagementAccess()) return next({ name: 'overview' })
            next()
          },
          component: () => import('@/components/dash/presence-management/BulkSpecialHoursEditor')
        },
        {
          path: 'presence-management-location/:id',
          name: 'presence-management-location',
          beforeEnter: (_to, _from, next) => {
            if (!checkPresenceManagementAccess()) return next({ name: 'overview' })
            next()
          },
          component: () => import('@/components/dash/presence-management/LocationEditor')
        },
        {
          path: 'reporting',
          name: 'reporting',
          component: () => import('@/components/dash/section/Reporting')
        },
        {
          path: 'plan',
          name: 'plan',
          component: () => {
            // Plan2 in local/sandbox for tests
            // const isLocalOrSandbox =
            //   process.env.NODE_ENV !== 'production' ||
            //   window.location.href.includes('sandbox')
            // return isLocalOrSandbox
            //   ? import('@/components/dash/section/Plan2')
            //   : import('@/components/dash/section/Plan')
            return import('@/components/dash/section/Plan2')
          }
        },
        {
          path: 'postCreator',
          name: 'postCreator',
          component: () => import('@/components/dash/section/PostCreator'),
          props: {
            mode: 'create'
          } // Définit le mode par défaut comme 'create'
        },
        {
          path: 'postCreator/:mode',
          name: 'postCreator',
          component: () => import('@/components/dash/section/PostCreator'),
          props: true
        },
        {
          path: 'posts2',
          name: 'posts2',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version == 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/planner')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts2'),
          props: route => {
            console.log('props posts2', route)
            return {
              ...route.query,
              ...route.params
            }
          }
        }
      ]
    },
    {
      // Public, unauthenticated Post Creator demo - deliberately outside the /dash branch above,
      // so it doesn't inherit its beforeEnter (which requires localStorage.token) or the global
      // beforeEach's selectPage/fetchData dispatch (which would try to fetch real pages).
      name: 'demoPostCreator',
      path: '/demo/postCreator',
      component: () => import('@/components/dash/section/DemoPostCreator'),
      // Not ready to be public yet - kept reachable in dev/sandbox to keep working on it, but
      // redirected away in prod until this is explicitly turned back on (see
      // checkPresenceManagementAccess above for the same NODE_ENV pattern elsewhere in this file).
      beforeEnter: (_to, _from, next) => {
        if (process.env.NODE_ENV === 'production') return next({ name: 'auth' })
        next()
      }
    },
    {
      name: 'auth',
      path: '/',
      component: () => import('@/views/Auth'),
      beforeEnter: (_to, _from, next) => {
        if (localStorage.token) {
          //lien profond, (temporaire)
          const version = localStorage.getItem('version_')
          if (version !== 'old') {
            const queryParams = Object.fromEntries(
              new URLSearchParams(window.location.search)
            )
            if (
              queryParams.post_id &&
              queryParams.page_id &&
              queryParams.bot_id &&
              !queryParams.no_redirection
            ) {
              location.href = `/#/dash/posts2${window.location.search}`
              return
            }
          }

          return next({ name: 'dash' })
        }
        next()
      },
      children: [
        {
          path: 'confirm',
          name: 'confirm'
        }
      ]
    },
    {
      name: 'onboarding',
      path: '/onboarding',
      component: () => import('@/views/Onboarding'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Onboarding is restricted to Admin users only
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (!user || !user.admin) return next({ name: 'dash' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      },
      children: [
        {
          path: '',
          redirect: 'needs'
        },
        {
          path: 'needs',
          name: 'onboarding-needs',
          component: () => import('@/components/onboarding/steps/StepNeeds')
        },
        {
          path: 'networks',
          name: 'onboarding-networks',
          component: () => import('@/components/onboarding/steps/StepNetworks')
        },
        {
          path: 'features',
          name: 'onboarding-features',
          component: () => import('@/components/onboarding/steps/StepFeatures')
        },
        {
          path: 'connectPages',
          name: 'onboarding-connectPages',
          component: () =>
            import('@/components/onboarding/steps/StepConnectPages')
        },
        {
          path: 'connectNatives',
          name: 'onboarding-connectNatives',
          component: () =>
            import('@/components/onboarding/steps/StepConnectNatives')
        },
        {
          path: 'connectExtensions',
          name: 'onboarding-connectExtensions',
          component: () =>
            import('@/components/onboarding/steps/StepConnectExtensions')
        },
        {
          path: 'summary',
          name: 'onboarding-summary',
          component: () =>
            import('@/components/onboarding/steps/StepSummary')
        },
        {
          path: 'payment',
          name: 'onboarding-payment',
          component: () =>
            import('@/components/onboarding/steps/StepPayment')
        }
      ]
    },
    {
      name: 'payment-regulation',
      path: '/payment-regulation',
      component: () => import('@/views/PaymentRegulation'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Payment regulation is restricted to Admin users only
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (!user || !user.admin) return next({ name: 'dash' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      }
    },
    {
      name: 'payment-blocked',
      path: '/payment-blocked',
      component: () => import('@/views/PaymentBlocked'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Only non-admin users are redirected here — admins go to /payment-regulation
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (user && user.admin) return next({ name: 'payment-regulation' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      }
    },
    {
      name: 'invite',
      path: '/invite',
      redirect: '/',
      component: () => import('@/views/Invite'),
      children: [
        {
          path: 'shared-planner',
          name: 'shared-planner',
          component: () => import('@/components/dash/section/SharedPlanner')
        },
        {
          path: 'viewer',
          name: 'viewer',
          component: () => import('@/components/dash/section/ViewerLogin')
        }
      ]
    },
    {
      name: 'legal',
      path: '/',
      redirect: '/',
      component: () => import('@/views/Invite'),
      children: [
        {
          path: 'legal',
          name: 'legal',
          component: () => import('@/components/dash/section/Legal')
        }
      ]
    },
    {
      name: 'preview',
      path: '/preview',
      component: () => import('@/views/Preview'),
      children: [
        {
          path: 'story/:idStorySelected?',
          name: 'story',
          component: () => import('@/components/preview/Story'),
          props: route => ({
            idStorySelected: route.params.idStorySelected,
            preview: route.query.preview === 'true' // ?preview=true
          })
        },

        {
          path: 'post/:pageIdSelected?',
          name: 'post',
          component: () => import('@/components/preview/Post'),
          props: route => ({ pageIdSelected: route.params.pageIdSelected })
        }
      ]
    }
  ]
})

// Target of the navigation in flight, so router.onError can reload straight onto it
let pendingTargetHref = null
let pendingTargetName = null

router.beforeEach((to, from, next) => {
  pendingTargetHref = router.resolve(to.fullPath).href
  pendingTargetName = to.name

  // A newer deploy is live: swap versions on a real page change (not the initial load, not a query-only
  // change) by loading the target page fresh, rather than reloading the tab while the user works.
  // Never when leaving postCreator: publishing navigates while its requests are in flight and the RequestManager rows aren't added yet.
  if (isUpdatePending() && from.matched.length && to.path !== from.path && from.name !== 'postCreator' && !isReloadBlocked()) {
    reloadAt(pendingTargetHref)
    return next(false)
  }
  checkForUpdate()

  // Check if returning from extension (onboarding mode)
  const extensionReturnStr = localStorage.getItem('mashup_extension_return')

  if (extensionReturnStr && to.name !== 'onboarding-connectExtensions') {
    try {
      const returnData = JSON.parse(extensionReturnStr)
      const age = Date.now() - returnData.timestamp

      // If data is recent (less than 30 seconds) and we're not already going to extensions
      if (age < 30000 && returnData.success) {
        console.log('[Router Guard] Extension return detected, redirecting to onboarding-connectExtensions')
        return next({ name: 'onboarding-connectExtensions' })
      } else if (age >= 30000) {
        // Clean up old data
        console.log('[Router Guard] Extension return data too old, cleaning up')
        localStorage.removeItem('mashup_extension_return')
      }
    } catch (error) {
      console.error('[Router Guard] Error parsing extension return:', error)
      localStorage.removeItem('mashup_extension_return')
    }
  }

  // Block all navigation when subscription is inactive (only for users who completed onboarding,
  // to avoid blocking new users who don't have a plan yet mid-onboarding)
  // const inProduction = process.env.NODE_ENV !== 'production'
  const inProduction = true // Enabled for all environments (remove to restrict to local/sandbox only)
  const onboardingDone =
    localStorage.getItem('onboardingCompleted') === 'true' ||
    localStorage.getItem('onboardingSkipped') === 'true'
  if (inProduction && onboardingDone && to.name !== 'payment-blocked' && to.name !== 'payment-regulation' && to.name !== 'auth') {
    try {
      const raw = localStorage.getItem('user')
      if (raw && raw !== 'undefined') {
        const user = JSON.parse(raw)
        if (user && user._premium && !user._premium.active) {
          if (user.admin) {
            return next({ name: 'payment-regulation' })
          } else {
            return next({ name: 'payment-blocked' })
          }
        }
      }
    } catch (e) { /* ignore */ }
  }

  // The demo route commits its own fictional pages (DemoPostCreator.vue) - never overwrite them
  // with a real (or failed) /bots + /pages fetch, which would also defeat the point of never
  // exposing real page ids to an unauthenticated visitor.
  if (to.name !== 'demoPostCreator') {
    store.dispatch('selectPage/fetchData')
    store.commit('selectPage/loadPagesSelectedFromLocalStorage')
  }
  next()
})

router.afterEach((to) => trackClarityPage(to))

// Sections where showing "resume your demo post?" actually makes sense - not tied to the exact
// moment of login (see attemptResumeDemoDraft's comment on why that was unreliable). Every
// navigation into one of these re-checks localStorage directly, so it doesn't matter whether the
// visitor's login involved a hard reload, multiple redirects, or landed straight on one of these.
// Whether it keeps re-asking after that is entirely up to the visitor's own answer in the popup
// (see below) - this guard itself has no memory of having asked before.
const RESUME_DEMO_DRAFT_ROUTES = ['planner', 'posts2', 'postCreator']

router.afterEach((to) => {
  if (!RESUME_DEMO_DRAFT_ROUTES.includes(to.name)) return
  if (!localStorage.getItem('demo/pendingDraft')) return
  if (resumeCheckInProgress) return
  resumeCheckInProgress = true

  attemptResumeDemoDraft()
    .then(resumed => {
      if (!resumed) return
      EventBus.$emit(
        'showModal',
        translate('dash.demo.resume-popup.title'),
        translate('dash.demo.resume-popup.subtitle'),
        translate('dash.demo.resume-popup.cta'),
        translate('dash.demo.resume-popup.close'),
        // Resuming always consumes the pending draft - there is nothing left to re-propose.
        () => {
          localStorage.removeItem('demo/pendingDraft')
          // A literal path, NOT {name: 'postCreator', params: {mode: 'draft'}} - 'postCreator' is
          // registered twice (path 'postCreator' with a hardcoded props:{mode:'create'}, and path
          // 'postCreator/:mode' with props:true), and vue-router's name->record map only keeps one
          // of them; pushing by name silently landed back on the hardcoded 'create' record, mode
          // param and all, which was the actual cause of the "empty form" this was meant to fix.
          // Every other "reopen this draft" call site in the app already uses this literal path
          // (drawer/index.vue, Posts.vue, PostCreator.vue's own draftToRestore) for that reason.
          router.push('/dash/postCreator/draft')
        },
        // Closing only consumes it if the visitor explicitly asked not to be asked again -
        // otherwise it stays, and the next navigation into one of these routes asks again.
        (dontAskAgain) => {
          if (dontAskAgain) localStorage.removeItem('demo/pendingDraft')
        },
        null,
        false,
        translate('dash.demo.resume-popup.dont-ask-again')
      )
    })
    .finally(() => { resumeCheckInProgress = false })
})

// Recover from stale chunks after a new deploy (a chunk the open tab's runtime still references is
// missing, often surfacing as "reading 'call'" instead of a clean 404): reload once onto the target page.
export function recoverFromStaleChunkAt(targetHref, targetName, error) {
  const recovered = recoverFromStaleChunk(targetHref, () => {
    // Reloading onto postCreator itself: autosave now and auto-restore after the reload (PostCreator.vue's mounted())
    if (router.currentRoute.name === 'postCreator' && targetName === 'postCreator') {
      EventBus.$emit('postcreator/forceSaveDraftBeforeReload')
      sessionStorage.setItem('mashup_reload_restore_draft', '1')
    }
  })
  if (recovered) console.warn('[Router] Stale chunk after deploy detected, reloading to recover:', error)
  else console.error('[Router] Stale chunk error persisted after reload, giving up:', error)
}

router.onError((error) => {
  if (!isStaleChunkError(error)) {
    console.error('[Router] Navigation error:', error)
    return
  }
  recoverFromStaleChunkAt(pendingTargetHref, pendingTargetName, error)
})

// Lazy components inside an already-loaded page: reload the current page itself
setStaleChunkHandler((error) => {
  recoverFromStaleChunkAt(router.resolve(router.currentRoute.fullPath).href, router.currentRoute.name, error)
})

export default router
